import { createFileRoute, Outlet, redirect, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import type { RouterContext } from '@shared/config/router-context';
import { useAbility } from '@features/authorization';
import { useAuth } from '@features/auth';
import { Header } from '@widgets/header';
import { canAccessInstructorWorkspace } from '@shared/lib/navigation';

/**
 * Instructor Section Layout
 *
 * Route: /:locale/instructor/*
 * Wraps all instructor pages with dashboard layout
 *
 * This file is automatically code-split by TanStack Router.
 */
export const Route = createFileRoute('/$locale/_authenticated/instructor')({
  beforeLoad: ({ context, params }) => {
    const { auth, ability } = context as RouterContext;

    if (auth.isLoading || !auth.isAuthenticated) {
      return;
    }

    if (!canAccessInstructorWorkspace(ability)) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({
        to: '/$locale/unauthorized',
        params: { locale: params.locale },
      });
    }
  },
  component: InstructorLayout,
});

function InstructorLayout() {
  const { locale } = Route.useParams();
  const { isLoading, isAuthenticated } = useAuth();
  const ability = useAbility();
  const navigate = useNavigate();

  useEffect(() => {
    if (isLoading || !isAuthenticated) {
      return;
    }

    if (!canAccessInstructorWorkspace(ability)) {
      void navigate({
        to: '/$locale/unauthorized',
        params: { locale },
        replace: true,
      });
    }
  }, [ability, isAuthenticated, isLoading, locale, navigate]);

  return (
    <div className="flex min-h-full flex-col pt-16 min-[992px]:pt-0">
      <Header locale={locale} className="min-[992px]:hidden" />

      <main className="min-w-0 p-6">
        <Outlet />
      </main>
    </div>
  );
}
